import { useState, useRef, useEffect, useCallback } from 'react'
import { PageHeader } from '../components/UI'
import { get } from '../utils/api'
import { IconPrinter, IconRefresh } from '@tabler/icons-react'

const ITEMS = [
  { type: 'round_table', label: 'Round Table (8)', w: 60, h: 60, shape: 'circle', color: '#206bc4' },
  { type: 'rect_table', label: 'Rect Table (6)', w: 80, h: 40, shape: 'rect', color: '#1a56a0' },
  { type: 'chair', label: 'Chair', w: 20, h: 20, shape: 'circle', color: '#64748b' },
  { type: 'podium', label: 'Podium', w: 40, h: 30, shape: 'rect', color: '#7c3aed' },
  { type: 'stage', label: 'Stage', w: 160, h: 60, shape: 'rect', color: '#1e293b' },
  { type: 'bar', label: 'Bar', w: 100, h: 30, shape: 'rect', color: '#b45309' },
  { type: 'dancefloor', label: 'Dance Floor', w: 120, h: 120, shape: 'rect', color: '#0f766e' },
]

export default function SpacePage() {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)
  const [spaces, setSpaces] = useState([])
  const [room, setRoom] = useState(null)
  const [elements, setElements] = useState([])
  const draggingRef = useRef(null)
  const offsetRef = useRef({ x: 0, y: 0 })
  const [selected, setSelected] = useState(null)
  const [canvasSize, setCanvasSize] = useState({ w: 700, h: 450 })

  useEffect(() => {
    get('/spaces').then(data => { setSpaces(data); if (data.length) setRoom(data[0]) }).catch(() => {})
  }, [])

  // Resize canvas to fit container
  useEffect(() => {
    const resize = () => {
      if (containerRef.current) {
        const w = containerRef.current.clientWidth - 16
        setCanvasSize({ w, h: Math.max(400, Math.round(w * 0.6)) })
      }
    }
    resize()
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [])

  const draw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const W = canvas.width, H = canvas.height
    ctx.clearRect(0, 0, W, H)

    // Grid
    ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 0.5
    for (let x = 0; x < W; x += 20) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke() }
    for (let y = 0; y < H; y += 20) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke() }

    // Room boundary
    ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 2; ctx.setLineDash([6, 4])
    ctx.strokeRect(20, 20, W - 40, H - 40); ctx.setLineDash([])

    // Room label
    if (room) {
      ctx.fillStyle = '#94a3b8'; ctx.font = '12px system-ui'; ctx.textAlign = 'left'
      ctx.fillText(room.name || 'Room', 28, 16)
    }

    // Elements
    elements.forEach((el, i) => {
      ctx.fillStyle = el.color + (i === selected ? 'ee' : 'bb')
      ctx.strokeStyle = i === selected ? '#1e40af' : 'transparent'; ctx.lineWidth = 2
      if (el.shape === 'circle') {
        ctx.beginPath(); ctx.arc(el.x + el.w / 2, el.y + el.h / 2, el.w / 2, 0, Math.PI * 2); ctx.fill(); ctx.stroke()
      } else {
        ctx.fillRect(el.x, el.y, el.w, el.h); ctx.strokeRect(el.x, el.y, el.w, el.h)
      }
      ctx.fillStyle = '#fff'; ctx.font = '10px system-ui'; ctx.textAlign = 'center'
      ctx.fillText(el.label.split('(')[0].trim(), el.x + el.w / 2, el.y + el.h / 2 + 3)
    })
  }, [elements, selected, room])

  useEffect(() => { draw() }, [draw, canvasSize])

  const addItem = (item) => {
    const cx = canvasSize.w / 2 - item.w / 2 + (Math.random() - 0.5) * 200
    const cy = canvasSize.h / 2 - item.h / 2 + (Math.random() - 0.5) * 150
    setElements(prev => [...prev, { ...item, x: Math.max(25, cx), y: Math.max(25, cy), id: Date.now() }])
  }

  const getCanvasXY = (e) => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    }
  }

  const handleMouseDown = (e) => {
    const { x: mx, y: my } = getCanvasXY(e)
    for (let i = elements.length - 1; i >= 0; i--) {
      const el = elements[i]
      if (mx >= el.x && mx <= el.x + el.w && my >= el.y && my <= el.y + el.h) {
        draggingRef.current = i
        offsetRef.current = { x: mx - el.x, y: my - el.y }
        setSelected(i)
        return
      }
    }
    setSelected(null)
  }

  const handleMouseMove = (e) => {
    if (draggingRef.current === null) return
    const { x: mx, y: my } = getCanvasXY(e)
    const idx = draggingRef.current
    const ox = offsetRef.current.x
    const oy = offsetRef.current.y
    setElements(prev => prev.map((el, i) => i === idx ? { ...el, x: mx - ox, y: my - oy } : el))
  }

  const handleMouseUp = () => { draggingRef.current = null }

  return (
    <>
      <PageHeader title="Space Designer" subtitle="Click elements to place, then drag to position"
        actions={<>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => { setElements([]); setSelected(null) }}><IconRefresh size={16} className="me-1" />Clear</button>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => window.print()}><IconPrinter size={16} className="me-1" />Print</button>
        </>} />
      <div className="d-flex align-items-center gap-2 mb-3">
        <select className="form-select form-select-sm" style={{ maxWidth: 260 }} value={room?.name || ''} onChange={e => setRoom(spaces.find(r => r.name === e.target.value))}>
          {spaces.map(r => <option key={r.name} value={r.name}>{r.name} ({(r.sqft || 0).toLocaleString()} sqft)</option>)}
        </select>
        {room && <span className="text-secondary" style={{ fontSize: '0.8rem' }}>Theater: {room.capacity_theater} | Banquet: {room.capacity_banquet} | Classroom: {room.capacity_classroom}</span>}
      </div>
      <div className="row">
        <div className="col-auto" style={{ width: 200 }}>
          <div className="card">
            <div className="card-header"><h4 className="card-title">Elements</h4></div>
            <div className="list-group list-group-flush">
              {ITEMS.map(item => (
                <button key={item.type} className="list-group-item list-group-item-action d-flex align-items-center gap-2" onClick={() => addItem(item)}>
                  <span className="avatar avatar-xs rounded" style={{ background: item.color, width: 16, height: 16 }} />{item.label}
                </button>
              ))}
            </div>
            {selected !== null && (
              <div className="card-footer">
                <button className="btn btn-danger btn-sm w-100" onClick={() => { setElements(prev => prev.filter((_, i) => i !== selected)); setSelected(null) }}>Delete Selected</button>
              </div>
            )}
            <div className="card-footer text-secondary" style={{ fontSize: '0.8rem' }}>{elements.length} items placed</div>
          </div>
        </div>
        <div className="col" ref={containerRef}>
          <div className="card"><div className="card-body p-2">
            <canvas ref={canvasRef} width={canvasSize.w} height={canvasSize.h}
              className="space-canvas" style={{ background: 'var(--tblr-bg-surface)', display: 'block', width: '100%', height: 'auto' }}
              onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} />
          </div></div>
        </div>
      </div>
    </>
  )
}
